import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteYellowAttachmentsCall,
  getYellowTransferDetails,
  postCreateYellowAdjustment,
  uploadYellowAttachmentsCall
} from "src/api/sp";
import { yellowEditableDaysLimit } from "src/config/constants/genericConstants";
import { resetStore } from "src/store/actions";
import { daysBetween } from "src/utils/genericFunctions";
import { getFormattedDate } from "src/utils/getDates";

export const createYellow = createAsyncThunk("createYellow", (payload: any) =>
  postCreateYellowAdjustment(payload)
);

export const getYellowDetails = createAsyncThunk(
  "yellowDetails",
  (payload: any) => getYellowTransferDetails(payload)
);

export const uploadYellowAttachments = createAsyncThunk(
  "uploadYellowAttachments",
  (payload: any) => uploadYellowAttachmentsCall(payload)
);

export const deleteYellowAttachments = createAsyncThunk(
  "deleteYellowAttachments",
  (filePath: string) => deleteYellowAttachmentsCall(filePath)
);

const initialState: any = {
  items: [],
  itemsCopy: [],
  notes: "",
  blockNavigation: true,
  loading: false,
  loadedYellowDetails: false,
  reason: "",
  dispositionInstructions: "",
  attachments: [],
  docId: "",
  apiStatus: "idle",
  notesCopy: "",
  attachmentsCopy: [],
  yellowStatus: "",
  submittedDate: null
};

const isNumeric = (num: any) =>
  (typeof num === "number" || typeof num === "string") && !isNaN(num as number);

const createYellowSlice = createSlice({
  name: "createYellow",
  initialState,
  reducers: {
    updateNavigationBlocking(state: any, action: any) {
      state.blockNavigation = action.payload;
    },
    updateNotes(state: any, action: any) {
      const notes = action.payload;
      state.notes = notes;
    },
    reset() {
      return initialState;
    },
    updateOnHand(state: any, action: any) {
      const alertItem = action.payload;
      const { itemId, onHandQuantity } = alertItem;

      // eslint-disable-next-line no-useless-escape
      const parsedVal = onHandQuantity.replace(/\d+/g, "").trim();

      // Test if number is numeric
      if (!isNumeric(parsedVal)) {
        return;
      }

      if (onHandQuantity?.length > 10) {
        return;
      }

      let itemIdx = state.items.findIndex(
        (item: any) => item.itemId === itemId
      );
      state.items[itemIdx].actualOnHand = onHandQuantity;

      itemIdx = state.itemsCopy.findIndex(
        (item: any) => item.itemId === itemId
      );
      state.itemsCopy[itemIdx].actualOnHand = onHandQuantity;
    },
    searchItems(state: any, action: any) {
      const { searchQuery } = action.payload;
      state.items = state?.itemsCopy?.filter(
        (item: any) =>
          item?.itemId?.toString().includes(searchQuery) ||
          item?.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    },
    sortItems(state: any, action: any) {
      const { sortParam, sortOrder } = action.payload;
      const items = [...state.items];

      items.sort((a: any, b: any) => {
        if (a[sortParam] < b[sortParam]) {
          return sortOrder === "ASC" ? -1 : 1;
        }
        if (a[sortParam] > b[sortParam]) {
          return sortOrder === "ASC" ? 1 : -1;
        }
        return 0;
      });

      state.items = items;
    },
    addAttachment(
      state: any,
      action: {
        payload: { fileName: string; fileURL: string; fileType: string };
      }
    ) {
      state.attachments.push(action.payload);
    },
    removeAttachment(state: any, action: { payload: number }) {
      const filterAttachments = state.attachments.filter(
        (_item: any, itemIndex: number) => itemIndex !== action?.payload
      );
      state.attachments = filterAttachments;
    },
    updateCreatedBy(state: any, action: any) {
      state.createdBy = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(createYellow.pending, (state) => {
        state.loading = true;
      })
      .addCase(createYellow.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createYellow.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getYellowDetails.pending, (state) => {
        state.loading = true;
        state.apiStatus = "pending";
      })
      .addCase(getYellowDetails.fulfilled, (state, action) => {
        try {
          const payload = action.payload;
          const receivingData = payload.data;
          const items = receivingData?.item.map((item: any, idx: number) => ({
            line: idx + 1,
            itemId: item.itemNumber,
            item: item.itemId,
            description: item.description,
            upc: item.upc,
            lotCode: item.lotCodes,
            estimatedOnHand: item.estimatedOnHand,
            actualOnHand:
              receivingData?.status === "Open" ? "" : item.actualOnHandQuantity,
            initialActualOnHandQuantity: item.initialActualOnHandQuantity,
            expiry: item?.expiry ? getFormattedDate(item.expiry) : ""
          }));
          state.yellowStatus = receivingData?.status;
          state.items = items;
          state.notes = receivingData?.notes || "";
          state.notesCopy = receivingData?.notes || "";
          state.itemsCopy = items;
          state.docId = receivingData.internalId;
          state.attachments = receivingData?.attachments;
          state.attachmentsCopy = receivingData?.attachments;
          state.reason = receivingData.reason;
          state.dispositionInstructions = receivingData.dispositionInstruction;

          state.isEditable =
            daysBetween(receivingData.receivedDate, new Date().toISOString()) <
            yellowEditableDaysLimit;
          state.adjustmentCreatedBy = receivingData.adjustmentCreatedBy;
          state.submittedDate = receivingData.submittedDate;
          state.apiStatus = "idle";
        } catch (e) {
          console.error(e);
          state.apiStatus = "error";
        }
        state.loading = false;
      })
      .addCase(getYellowDetails.rejected, (state) => {
        state.loading = false;
        state.apiStatus = "error";
      });
  }
});

export const {
  updateNotes,
  updateNavigationBlocking,
  reset,
  updateOnHand,
  searchItems,
  sortItems,
  addAttachment,
  removeAttachment,
  updateCreatedBy
} = createYellowSlice.actions;

export const selectItems = (state: any) => state.createYellow.items;

export const selectBlockNavigation = (state: any) =>
  state.createYellow.blockNavigation;

export const selectNotes = (state: any) => state.createYellow.notes;

export const selectLoading = (state: any) => state.createYellow.loading;

export default createYellowSlice.reducer;
